// ===========================================
// PRODUCTION (food makers / CPG)
// ===========================================
// Raw orders → "what to make": totals by class / flavor / destination, an
// exceptions queue for unmapped products, printable labels, and the mapping
// setup. Feature-flagged (settings.modules.production) — restaurants never
// see it.

import { useState, useEffect } from 'react';
import ProcurementNav from '../components/ProcurementNav';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api, { productionApi, restaurantApi } from '../services/api';
import SupplyPlan from '../components/SupplyPlan';
import { Plus, X, Trash2, AlertTriangle, CalendarDays, Factory, Tag, ShoppingBag, RefreshCw, Check, Pencil, MapPin, Send, FileSpreadsheet } from 'lucide-react';
import clsx from 'clsx';
import WholesaleMakePlan from '../components/WholesaleMakePlan';
import { CpgProductionOutlook } from '../components/CpgForecast';
import { isCpgSettings } from '../utils/cpg';
import CpgFulfillment from '../components/CpgFulfillment';

const fmtDate = (v?: string | null) => (v ? new Date(v).toLocaleDateString() : '—');
type Tab = 'plan' | 'supply' | 'orders' | 'deliveries' | 'mappings';

export default function ProductionPage() {
  const workspaceId = (() => {
    try { return JSON.parse(localStorage.getItem('manager_current_restaurant') || 'null')?.id || null; } catch { return null; }
  })();
  const restaurant = useQuery({
    queryKey: ['restaurant-current', workspaceId],
    queryFn: async () => (await restaurantApi.getCurrent()).data?.data || null,
    staleTime: 30_000,
  });
  // Resolve the business before mounting any bakery queries or controls.
  if (restaurant.isPending) return <div className="p-8 text-sm text-slate-500">Loading operations…</div>;
  if (!restaurant.data) return <div className="p-8 text-sm text-slate-600">Could not load this workspace. <button onClick={() => { void restaurant.refetch(); }} className="underline">Try again</button></div>;
  const settings = restaurant.data.settings;
  if (isCpgSettings(settings) && !settings?.channelForecast && !settings?.overviewSeries && !Object.keys(settings?.supplyRatios || {}).length) {
    return <CpgFulfillment key={workspaceId} workspaceId={workspaceId} name={restaurant.data.name} />;
  }
  return <MakerProductionPage key={workspaceId} />;
}

function MakerProductionPage() {
  const qc = useQueryClient();
  // Deep links from the dashboard's quick actions: /production?tab=supply
  const urlTab = new URLSearchParams(window.location.search).get('tab') as Tab | null;
  const [tab, setTab] = useState<Tab>(urlTab && ['plan', 'supply', 'orders', 'deliveries', 'mappings'].includes(urlTab) ? urlTab : 'plan');
  const [banner, setBanner] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const [prepOpen, setPrepOpen] = useState(true);
  const [prepDone, setPrepDone] = useState<Record<string, boolean>>({});
  useEffect(() => {
    try { setPrepDone(JSON.parse(localStorage.getItem('mise-prep-done') || '{}')); } catch { /* fresh sheet */ }
  }, []);
  const togglePrep = (key: string) => {
    setPrepDone((d) => {
      const next = { ...d, [key]: !d[key] };
      try { localStorage.setItem('mise-prep-done', JSON.stringify(next)); } catch { /* storage full */ }
      return next;
    });
  };
  // Restaurant settings drive the CPG split below, so they load first. A CPG
  // brand never sees the weekday prep checklist — its "prep" is monthly
  // production against projected demand (CpgProductionOutlook); the detection
  // reads the same signals Layout.tsx keys off.
  const { data: restaurant } = useQuery({ queryKey: ['restaurant-forecast'], queryFn: async () => (await restaurantApi.getCurrent()).data?.data || null });
  const isCpgAccount = isCpgSettings(restaurant?.settings);
  const { data: prep } = useQuery({
    queryKey: ['prep-list'],
    queryFn: async () => (await api.get('/v1/inventory/prep-list')).data?.data,
    enabled: tab === 'plan' && !!restaurant && !isCpgAccount,
  });
  const say = (tone: 'success' | 'error', text: string) => setBanner({ tone, text });
  // Plan filters — all optional. Default: everything upcoming (no date), which
  // is why the plan is no longer empty when today isn't a bake day.
  // date = one exact day; start+end = a period (this week, a month) and wins.
  const [planFilters, setPlanFilters] = useState<{ date: string; start: string; end: string; destination: string; source: string }>({ date: '', start: '', end: '', destination: '', source: '' });
  // First load lands on the NEXT fulfilment day (the Friday bake), not the
  // all-upcoming firehose. Clearing filters shows everything upcoming again.
  const [defaultDateApplied, setDefaultDateApplied] = useState(false);
  // Flavor of the week gets a real name (settings.flavorOfWeekName) — the
  // plan, the CSV and the emailed breakdown all pick it up.
  const [fotwEditing, setFotwEditing] = useState(false);
  const [showPastOrders, setShowPastOrders] = useState(false);
  const [orderSearch, setOrderSearch] = useState('');
  const [stopSearch, setStopSearch] = useState('');
  const [fotwName, setFotwName] = useState('');
  const saveFotw = useMutation({
    mutationFn: () => restaurantApi.updateSettings({ flavorOfWeekName: fotwName.trim() }),
    onSuccess: () => { setFotwEditing(false); qc.invalidateQueries({ queryKey: ['production-plan'] }); },
  });

  // "Extras" buffer — the +N she adds to every meal's order count to get her
  // Final Count (mirrors the Extras column in her spreadsheet).
  const [bufferDraft, setBufferDraft] = useState<string | null>(null);
  const saveBuffer = useMutation({
    mutationFn: () => productionApi.setBuffer(Math.max(0, parseInt(bufferDraft ?? '0', 10) || 0)),
    onSuccess: () => { setBufferDraft(null); qc.invalidateQueries({ queryKey: ['production-plan'] }); say('success', 'Extras saved — final counts updated.'); },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't save extras."),
  });
  // Her Sheets flow: one CSV of the day's plan — totals, per-flavor with
  // batches, per-destination — pasted straight into the dough math.
  const downloadPlanCsv = () => {
    if (!plan) return;
    // Her format: one row per customer, a column per type, row totals, and
    // column totals at the very end — then the batch math underneath.
    const cols: string[] = (plan.byClass || []).map((c: any) => c.name);
    const rows: string[][] = [['customer', 'lane', ...cols, 'total units']];
    for (const r of plan.orderRows || []) {
      rows.push([r.customer, r.destination, ...cols.map((c) => String(r.counts?.[c] || 0)), String(r.total)]);
    }
    rows.push(['TOTAL', '', ...cols.map((c) => String((plan.byClass || []).find((x: any) => x.name === c)?.quantity || 0)), String(plan.totalUnits)]);
    rows.push([]);
    rows.push(['batches', plan.batchYield ? `${plan.totalBatches} batches of ${plan.batchYield}` : 'set a batch size in Settings']);
    for (const r of plan.byFlavor || []) rows.push(['by flavor', r.name, String(r.quantity), plan.batchYield ? `${Math.ceil(r.quantity / plan.batchYield)} batches` : '']);
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `what-to-make-${planFilters.date || 'upcoming'}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };


  const { data: plan } = useQuery({
    queryKey: ['production-plan', planFilters],
    queryFn: async () => (await productionApi.getPlan(planFilters)).data?.data || null,
    enabled: tab === 'plan',
  });
  useEffect(() => {
    if (defaultDateApplied || !plan?.options?.dates) return;
    setDefaultDateApplied(true);
    if (planFilters.date || planFilters.start || planFilters.destination || planFilters.source) return;
    const today = new Date().toISOString().slice(0, 10);
    const next = (plan.options.dates as Array<{ value: string }>).map((d) => d.value).filter((v) => v >= today).sort()[0];
    if (next) setPlanFilters((f) => ({ ...f, date: next }));
  }, [plan, defaultDateApplied, planFilters]);
  // Forecast plans are month-granular by nature — a batch is "September", not
  // "Tue Sep 30"; the stored day is just the loader's bucket. When the plan is
  // built purely from forecast rows, dates render as months. Real orders
  // (Shopify, subscriptions, hand-added) keep their exact dates.
  const planIsMonthly = (plan?.options?.sources || []).length === 1 && plan?.options?.sources?.[0] === 'forecast';
  const planDateLabel = (d: string, long = false) => planIsMonthly
    ? new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : new Date(d + 'T12:00:00').toLocaleDateString('en-US', { weekday: long ? 'long' : 'short', month: 'short', day: 'numeric' });
  // The headline names the day being prepped, from the same date filter the
  // plan is built on — "Prep for Wednesday". Past a week out the weekday alone
  // is ambiguous, so the date comes along.
  const prepHeadline = () => {
    const todayKey = new Date().toISOString().slice(0, 10);
    if (planFilters.date) {
      if (planIsMonthly) return `Prep for ${new Date(planFilters.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`;
      if (planFilters.date === todayKey) return 'Prep for today';
      const d = new Date(planFilters.date + 'T12:00:00');
      const daysOut = Math.round((d.getTime() - new Date(todayKey + 'T12:00:00').getTime()) / 86400000);
      return `Prep for ${d.toLocaleDateString('en-US', daysOut > 0 && daysOut <= 6 ? { weekday: 'long' } : { weekday: 'long', month: 'short', day: 'numeric' })}`;
    }
    if (planFilters.start) {
      const s = new Date(planFilters.start + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const e = new Date((planFilters.end || planFilters.start) + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      return `Prep for ${s} – ${e}`;
    }
    return 'Prep for everything upcoming';
  };
  const unitsLabel = (n: number) => `${n.toLocaleString('en-US')} unit${n === 1 ? '' : 's'}`;
  // You bake whole batches, so make-list rows round up.
  const batchesOf = (units: number) => {
    if (!plan?.batchYield) return '';
    const b = Math.ceil(units / plan.batchYield);
    return ` · ${b} batch${b === 1 ? '' : 'es'} of ${plan.batchYield}`;
  };
  const { data: orders = [] } = useQuery({
    queryKey: ['production-orders'],
    queryFn: async () => (await productionApi.getOrders()).data?.data || [],
  });
  const { data: mappings = [] } = useQuery({
    queryKey: ['production-mappings'],
    queryFn: async () => (await productionApi.getMappings()).data?.data || [],
  });
  // Deliveries + product-mapping only make sense when a store feeds this. Makers who
  // ship through distributors (no store connected) just see "What to make" + Orders.
  const { data: shopifyStatus } = useQuery({
    queryKey: ['production-shopify-status'],
    queryFn: async () => (await productionApi.getShopifyStatus()).data?.data || null,
  });
  const storeConnected = !!shopifyStatus?.connected;
  // Supply plan (forecast → gallons → containers) shows only for makers who have per-case yields set.
  const hasSupplyPlan = Object.keys(restaurant?.settings?.supplyRatios || {}).length > 0;
  const isCpg = restaurant?.settings?.businessType === 'cpg';
  // All-orders rows speak the same language as the plan: units after mapping.
  // Mirrors productionPlan.ts — trim+lowercase name match, active rules only,
  // units = line qty × each output's unitsPerQty.
  const activeMapByName = new Map<string, any>((mappings as any[]).filter((m: any) => m.isActive !== false).map((m: any) => [String(m.sourceName || '').trim().toLowerCase(), m]));
  const orderUnits = (o: any) => {
    let units = 0;
    let unmappedQty = 0;
    for (const l of (o.lines || []) as any[]) {
      const qty = parseFloat(String(l.quantity)) || 0;
      const m = activeMapByName.get(String(l.sourceName || '').trim().toLowerCase());
      if (!m) { unmappedQty += qty; continue; }
      for (const out of (m.outputs || []) as any[]) units += qty * (Number(out.unitsPerQty) || 1);
    }
    return { units, unmappedQty };
  };
  // An order row only talks batches once it would fill one, and a part-filled
  // batch stays a fraction — rounding up per order would overstate the bake.
  const settingsBatchYield = Number(restaurant?.settings?.batchYield) || null;
  const orderBatchesLabel = (units: number) => {
    if (!settingsBatchYield || units < settingsBatchYield) return '';
    const n = Math.round((units / settingsBatchYield) * 10) / 10;
    return ` · ${units % settingsBatchYield === 0 ? '' : '~'}${n} batch${n === 1 ? '' : 'es'} of ${settingsBatchYield}`;
  };
  const [openedOnDefault, setOpenedOnDefault] = useState(false);
  useEffect(() => {
    if (!storeConnected && (tab === 'deliveries' || tab === 'mappings')) setTab('plan');
    if (!hasSupplyPlan && tab === 'supply') setTab('plan');
    // A CPG's home view is the Supply plan (press & pallets), not the day list.
    if (isCpg && hasSupplyPlan && !openedOnDefault && !urlTab && tab === 'plan') { setTab('supply'); setOpenedOnDefault(true); }
  }, [storeConnected, hasSupplyPlan, isCpg, openedOnDefault, tab]);

  // ---- deliveries tracker
  const [deliveryDate, setDeliveryDate] = useState('');
  const [deliveryDateDefaulted, setDeliveryDateDefaulted] = useState(false);
  // Filters: by how the order arrived, and the Rosh Hashanah presale (round challah).
  const [deliverySource, setDeliverySource] = useState('');
  const [rhOnly, setRhOnly] = useState(false);
  const RH_RE = /rosh\s*hashan|round challah/i;

  // Tara's bag labels: every stop of the day onto Avery 8160 (30/sheet, the
  // blanks already in her label printer). Name, what they ordered in the
  // words the packer knows ("1× Challah Duo"), address when it ships.
  // "What's on the sticker matches what's in the bag."
  const printBagLabels = () => {
    const stops = (deliveries?.byDestination || []).flatMap((g: any) => (g.orders || []).map((o: any) => ({ ...o, lane: g.destination })))
      .sort((a: any, b: any) => String(a.lane).localeCompare(String(b.lane)) || ((a.routeOrder ?? 9999) - (b.routeOrder ?? 9999)) || String(a.customerName || '').localeCompare(String(b.customerName || '')));
    if (!stops.length) return;
    const esc = (v: any) => String(v || '').replace(/&/g, '&amp;').replace(/</g, '&lt;');
    const label = (o: any) => `<div class="lb"><div class="nm">${o.routeOrder != null ? '#' + o.routeOrder + ' · ' : ''}${esc(o.customerName || 'Order')}${(o.units || 0) > 1 ? ' · BAG' : ''}</div><div class="it">${esc(o.items)}</div>${o.deliveryAddress ? `<div class="ad">${esc(o.deliveryAddress)}</div>` : `<div class="ad">${esc(o.lane)} pickup</div>`}</div>`;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<!doctype html><html><head><title>Bag labels — ${deliveryDate || 'all dates'}</title><style>
      @page { size: letter; margin: 0.5in 0.1875in; }
      * { box-sizing: border-box; margin: 0; }
      body { font-family: -apple-system, Helvetica, sans-serif; }
      .sheet { display: grid; grid-template-columns: repeat(3, 2.625in); column-gap: 0.125in; }
      .lb { width: 2.625in; height: 1in; padding: 0.07in 0.12in; overflow: hidden; page-break-inside: avoid; }
      .nm { font-weight: 700; font-size: 9.5pt; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      .it { font-size: 8pt; line-height: 1.2; max-height: 0.32in; overflow: hidden; }
      .ad { font-size: 7.5pt; color: #333; line-height: 1.15; max-height: 0.3in; overflow: hidden; }
    </style></head><body><div class="sheet">${stops.map(label).join('')}</div><script>window.onload=function(){window.print()}<\/script></body></html>`);
    w.document.close();
  };
  const stopVisible = (o: any) => (!deliverySource || o.source === deliverySource) && (!rhOnly || RH_RE.test(o.items || ''))
    && (!stopSearch.trim() || [o.customerName, o.deliveryAddress, o.items].some((v: any) => String(v || '').toLowerCase().includes(stopSearch.trim().toLowerCase())));
  const { data: deliveries } = useQuery({
    queryKey: ['production-deliveries', deliveryDate],
    queryFn: async () => (await productionApi.getDeliveries(deliveryDate || undefined)).data?.data || null,
    enabled: tab === 'deliveries',
  });
  useEffect(() => {
    // Land on the NEXT delivery day, not every stop ever made. Past days stay
    // one dropdown pick away.
    if (deliveryDateDefaulted || !deliveries?.dates?.length || deliveryDate) return;
    setDeliveryDateDefaulted(true);
    const today = new Date().toISOString().slice(0, 10);
    const next = (deliveries.dates as string[]).filter((d) => d >= today).sort()[0];
    if (next) setDeliveryDate(next);
  }, [deliveries, deliveryDate, deliveryDateDefaulted]);
  const setDelivery = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'pending' | 'out' | 'delivered' }) => productionApi.setDeliveryStatus(id, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['production-deliveries'] }),
  });
  // Quick per-stop edit (customer + address) right in the tracker.
  const [editStop, setEditStop] = useState<{ id: string; customerName: string; address: string } | null>(null);
  const [confirmDelStop, setConfirmDelStop] = useState<string | null>(null);
  const saveStop = useMutation({
    mutationFn: () => productionApi.updateOrder(editStop!.id, { customerName: editStop!.customerName || null, deliveryAddress: editStop!.address || null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['production-deliveries'] });
      qc.invalidateQueries({ queryKey: ['production-orders'] });
      setEditStop(null);
      say('success', 'Stop updated.');
    },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't update the stop."),
  });
  // Remove a stop from the run. It's the underlying order, so it also leaves the bake plan.
  const deleteStop = useMutation({
    mutationFn: (id: string) => productionApi.deleteOrder(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['production-deliveries'] });
      qc.invalidateQueries({ queryKey: ['production-orders'] });
      qc.invalidateQueries({ queryKey: ['production-plan'] });
      setEditStop(null);
      say('success', 'Stop removed.');
    },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't remove the stop."),
  });

  // ---- optimized itinerary → email
  const [routeLane, setRouteLane] = useState<string | null>(null);
  const [routeForm, setRouteForm] = useState({ startAddress: '', email: '' });
  const createRoute = useMutation({
    mutationFn: () => productionApi.createItinerary({
      destination: routeLane!,
      startAddress: routeForm.startAddress.trim(),
      email: routeForm.email.trim(),
      date: deliveryDate || undefined,
    }),
    onSuccess: (res: any) => {
      const d = res?.data?.data;
      setRouteLane(null);
      const to = Array.isArray(d?.emailedTo) ? d.emailedTo.join(', ') : d?.emailedTo;
      say('success', `Itinerary for ${d?.destination} (${d?.stops?.length} stops, ~${d?.totalKm} km) emailed to ${to}.${d?.skippedNoAddress ? ` ${d.skippedNoAddress} stop${d.skippedNoAddress === 1 ? '' : 's'} left out — no delivery address; add it with the pencil and re-run.` : ''}`);
    },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't build or send the route."),
  });

  // ---- email the "what to make" breakdown (reliable path, no chat needed)
  const [showEmailPlan, setShowEmailPlan] = useState(false);
  const [emailPlanTo, setEmailPlanTo] = useState('');
  const emailPlan = useMutation({
    mutationFn: () => productionApi.emailPlanBreakdown({ date: planFilters.date || undefined, email: emailPlanTo.trim() }),
    onSuccess: (res: any) => { const d = res?.data?.data; setShowEmailPlan(false); setEmailPlanTo(''); say('success', `Emailed the ${d?.units}-unit breakdown to ${(d?.sentTo || []).join(', ')}.`); },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't email the breakdown."),
  });

  // ---- Shopify connection
  const { data: shopify } = useQuery({
    queryKey: ['production-shopify'],
    queryFn: async () => (await productionApi.getShopifyStatus()).data?.data || { connected: false },
  });
  const [showConnect, setShowConnect] = useState(false);
  const [connForm, setConnForm] = useState({ shopDomain: '', accessToken: '' });
  const connectShopify = useMutation({
    mutationFn: () => productionApi.connectShopify({
      shopDomain: connForm.shopDomain.trim(),
      accessToken: connForm.accessToken.trim(),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['production-shopify'] });
      setShowConnect(false);
      setConnForm({ shopDomain: '', accessToken: '' });
      say('success', 'Shopify connected. Syncing your orders now…');
      syncShopify.mutate();
    },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't connect to Shopify."),
  });
  const startShopifyOauth = useMutation({
    mutationFn: () => productionApi.startShopifyOauth(connForm.shopDomain.trim()),
    onSuccess: (res: any) => {
      const url = res?.data?.data?.url;
      if (url) window.location.href = url; // straight to the Shopify approve screen
      else say('error', "Couldn't start the Shopify connection.");
    },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't start the Shopify connection."),
  });
  const syncShopify = useMutation({
    mutationFn: () => productionApi.syncShopify(),
    onSuccess: (res: any) => {
      qc.invalidateQueries({ queryKey: ['production-orders'] });
      qc.invalidateQueries({ queryKey: ['production-plan'] });
      qc.invalidateQueries({ queryKey: ['production-shopify'] });
      const d = res?.data?.data || {};
      const n = d.synced ?? 0;
      const subs = d.subscriptions ?? 0;
      let msg = n > 0 ? `Synced ${n} open order${n === 1 ? '' : 's'} from Shopify` : 'No open orders in Shopify right now';
      if (subs > 0) msg += ` · ${subs} upcoming subscription${subs === 1 ? '' : 's'}`;
      say(d.subscriptionWarning ? 'error' : 'success', d.subscriptionWarning ? `${msg}. Subscriptions: ${d.subscriptionWarning}` : `${msg}.`);
    },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't sync from Shopify."),
  });
  const disconnectShopify = useMutation({
    mutationFn: () => productionApi.disconnectShopify(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['production-shopify'] });
      say('success', 'Shopify disconnected. Synced orders stay until you clear them.');
    },
  });

  // ---- order form
  const today = new Date().toISOString().slice(0, 10);
  const [showOrderForm, setShowOrderForm] = useState(false);
  const emptyOrder = { customerName: '', destination: '', fulfillmentDate: today, lines: [{ sourceName: '', quantity: 0 }] };
  // Known product names from synced orders — hand-added lines that match these
  // parse into the right flavor and pickup/delivery lane on the Holiday board.
  const knownProducts = [...new Set((orders || []).flatMap((oo: any) => (oo.lines || []).map((l: any) => l.sourceName)).filter(Boolean))].sort() as string[];
  const [orderForm, setOrderForm] = useState(emptyOrder);
  const addOrder = useMutation({
    mutationFn: () => productionApi.addOrders({
      customerName: orderForm.customerName || undefined,
      destination: orderForm.destination || undefined,
      fulfillmentDate: orderForm.fulfillmentDate || undefined,
      lines: orderForm.lines.filter((l) => l.sourceName && l.quantity > 0),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['production-orders'] });
      qc.invalidateQueries({ queryKey: ['production-plan'] });
      setOrderForm(emptyOrder);
      setShowOrderForm(false);
      say('success', 'Order added.');
    },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't add the order."),
  });
  const deleteOrder = useMutation({
    mutationFn: (id: string) => productionApi.deleteOrder(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['production-orders'] }); qc.invalidateQueries({ queryKey: ['production-plan'] }); },
  });

  // ---- edit an order inline
  const [editOrderId, setEditOrderId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<{ customerName: string; destination: string; deliveryAddress: string; fulfillmentDate: string; lines: Array<{ sourceName: string; quantity: number }> }>({ customerName: '', destination: '', deliveryAddress: '', fulfillmentDate: '', lines: [] });
  const beginEdit = (o: any) => {
    setEditOrderId(o.id);
    setEditForm({
      customerName: o.customerName || '',
      destination: o.destination || '',
      deliveryAddress: o.deliveryAddress || '',
      fulfillmentDate: o.fulfillmentDate ? new Date(o.fulfillmentDate).toISOString().slice(0, 10) : '',
      lines: (o.lines || []).map((l: any) => ({ sourceName: l.sourceName, quantity: parseFloat(l.quantity) || 0 })),
    });
  };
  const saveOrder = useMutation({
    mutationFn: () => productionApi.updateOrder(editOrderId!, {
      customerName: editForm.customerName || null,
      destination: editForm.destination || null,
      deliveryAddress: editForm.deliveryAddress || null,
      fulfillmentDate: editForm.fulfillmentDate || null,
      lines: editForm.lines.filter((l) => l.sourceName && l.quantity > 0),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['production-orders'] });
      qc.invalidateQueries({ queryKey: ['production-plan'] });
      qc.invalidateQueries({ queryKey: ['production-deliveries'] });
      setEditOrderId(null);
      say('success', 'Order updated.');
    },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't update the order."),
  });

  // ---- accept all suggested mappings at once
  const applySuggestions = useMutation({
    mutationFn: () => productionApi.applySuggestions(),
    onSuccess: (res: any) => {
      qc.invalidateQueries({ queryKey: ['production-mappings'] });
      qc.invalidateQueries({ queryKey: ['production-plan'] });
      const n = res?.data?.data?.created ?? 0;
      say('success', n > 0 ? `Mapped ${n} product${n === 1 ? '' : 's'} from the suggestions.` : 'Nothing left to map.');
    },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't apply the suggestions."),
  });

  // ---- mapping form
  const [showMapForm, setShowMapForm] = useState(false);
  const emptyMap = { sourceName: '', outputs: [{ className: '', flavor: '', unitsPerQty: 1 }] };
  const [mapForm, setMapForm] = useState(emptyMap);
  const addMapping = useMutation({
    mutationFn: () => productionApi.createMapping({
      sourceName: mapForm.sourceName,
      outputs: mapForm.outputs.filter((o) => o.className).map((o) => ({ className: o.className, flavor: o.flavor || undefined, unitsPerQty: Number(o.unitsPerQty) || 1 })),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['production-mappings'] });
      qc.invalidateQueries({ queryKey: ['production-plan'] });
      setMapForm(emptyMap);
      setShowMapForm(false);
      say('success', 'Mapping added.');
    },
    onError: (e: any) => say('error', e?.response?.data?.error?.message || "Couldn't add the mapping."),
  });
  const deleteMapping = useMutation({
    mutationFn: (id: string) => productionApi.deleteMapping(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['production-mappings'] }); qc.invalidateQueries({ queryKey: ['production-plan'] }); },
  });

  // Quick "add mapping" from an exception — prefilled with the suggested rule.
  const mapException = (sourceName: string, suggested?: Array<{ className: string; flavor?: string; unitsPerQty: number }>) => {
    const outputs = suggested && suggested.length
      ? suggested.map((o) => ({ className: o.className, flavor: o.flavor || '', unitsPerQty: o.unitsPerQty || 1 }))
      : [{ className: '', flavor: '', unitsPerQty: 1 }];
    setMapForm({ sourceName, outputs });
    setShowMapForm(true);
    setTab('mappings');
  };
  const suggestLabel = (s?: Array<{ className: string; flavor?: string; unitsPerQty: number }>) =>
    (s || []).map((o) => `${o.unitsPerQty}× ${o.flavor || o.className}`).join(' + ');

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6 pb-safe">
      <ProcurementNav />
      <datalist id="known-products">
        {knownProducts.map((n) => <option key={n} value={n} />)}
      </datalist>
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-slate-900">Production</h1>
        <p className="text-sm md:text-base text-slate-500">Turn orders into a clear "what to make" — by product, flavor, and destination</p>
      </div>

      {banner && (
        <div className={clsx('flex items-start justify-between gap-3 rounded-xl border px-4 py-3 text-sm',
          banner.tone === 'success' ? 'border-green-200 bg-green-50 text-green-800' : 'border-amber-200 bg-amber-50 text-amber-800')}>
          <p>{banner.text}</p>
          <button onClick={() => setBanner(null)} className="shrink-0 opacity-60 hover:opacity-100"><X className="w-4 h-4" /></button>
        </div>
      )}

      <div className="flex gap-2 overflow-x-auto">
        {((isCpg
          ? [['supply', 'Supply plan'], ['plan', 'What to make'], ['orders', 'All orders'], ['deliveries', 'Deliveries'], ['mappings', 'Products & counting']]
          : [['plan', 'What to make'], ['supply', 'Supply plan'], ['orders', 'All orders'], ['deliveries', 'Deliveries'], ['mappings', 'Products & counting']]) as Array<[Tab, string]>)
          .filter(([id]) => (id !== 'supply' || hasSupplyPlan) && (storeConnected || (id !== 'deliveries' && id !== 'mappings')))
          .map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            className={clsx('px-4 py-2 rounded-full text-sm font-medium shrink-0', tab === id ? 'bg-primary-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')}>
            {label}
          </button>
        ))}
      </div>

      {/* One plain sentence per tab — what this screen is for. */}
      {tab !== 'supply' && (
        <p className="text-sm text-slate-500 -mt-1">
          {tab === 'plan' && 'Pick a day, check the split, send it to the team.'}
          {tab === 'orders' && 'Everything still owed, in units and batches. Synced from Shopify (subscriptions projected before they bill) or added by hand — the plan and deliveries build from these.'}
          {tab === 'deliveries' && 'The day\'s stops, lane by lane. Tap a status as you go; 🛍 means a multi-item stop — pack a bag.'}
          {tab === 'mappings' && 'How each product on an order counts toward the bake: a Duo becomes 1 plain + 1 flavor. The plan\'s math starts here.'}
        </p>
      )}

      {tab === 'supply' && <SupplyPlan />}

      {/* ---- PLAN ---- */}
      {tab === 'plan' && (
        <div className="space-y-4">
          {/* The day leads: which bake this is, how much it is, and the one
              action — send it to the team. The day picker lives here, not down
              in the filters. */}
          <div className="card p-5 bg-primary-50 border-primary-100">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="text-xl md:text-2xl font-bold text-slate-900">{prepHeadline()}</h2>
                {plan && plan.totalUnits > 0 && (
                  <p className="text-sm text-slate-600 mt-1">
                    Make {unitsLabel(plan.totalUnits)}{batchesOf(plan.totalUnits)} · from {plan.orderCount} order{plan.orderCount === 1 ? '' : 's'}
                  </p>
                )}
                {plan && plan.expectedUnits > 0 && (
                  <p className="text-sm text-violet-600 mt-1">{plan.confirmedUnits} confirmed · {plan.expectedUnits} expected from subscriptions (not billed yet)</p>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap shrink-0">
                <div className="flex items-center gap-1.5">
                  <CalendarDays className="w-4 h-4 text-slate-400" />
                  <select value={planFilters.date} onChange={(e) => setPlanFilters({ ...planFilters, date: e.target.value, start: '', end: '' })} className="input w-auto">
                    <option value="">{planIsMonthly ? 'All months' : 'All upcoming dates'}</option>
                    {(plan?.options?.dates || []).filter((d: any) => planIsMonthly || d.value >= new Date().toISOString().slice(0, 10)).map((d: any) => (
                      <option key={d.value} value={d.value}>{planDateLabel(d.value)} ({d.orders})</option>
                    ))}
                  </select>
                </div>
                {plan && plan.totalUnits > 0 && (
                  <button onClick={() => setShowEmailPlan((s) => !s)} className="btn btn-primary flex items-center gap-1.5" title="Email this make-list (e.g. to your baker or co-packer)"><Send className="w-4 h-4" /> Send to the team</button>
                )}
              </div>
            </div>
            {plan && plan.totalUnits > 0 && !planFilters.date && plan.byDate.length > 1 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {plan.byDate.map((d: any) => (
                  <button key={d.name} onClick={() => d.name !== 'No date' && setPlanFilters({ ...planFilters, date: d.name })}
                    className="px-2.5 py-1 rounded-full bg-white border border-primary-100 text-sm hover:border-primary-300">
                    {d.name === 'No date' ? 'No date' : planDateLabel(d.name)}: <span className="font-semibold">{d.quantity}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Narrower cuts — a period, a lane, an order type. Selects appear
              only when there's actually something to choose between. */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Free date range — from → to, any window you like */}
            <span className="text-xs text-slate-400">From</span>
            <input type="date" value={planFilters.start} onChange={(e) => setPlanFilters({ ...planFilters, date: '', start: e.target.value, end: planFilters.end && planFilters.end >= e.target.value ? planFilters.end : e.target.value })} className="input w-auto" />
            <span className="text-xs text-slate-400">to</span>
            <input type="date" value={planFilters.end} min={planFilters.start || undefined} onChange={(e) => setPlanFilters({ ...planFilters, date: '', end: e.target.value, start: planFilters.start || e.target.value })} className="input w-auto" />
            {(plan?.options?.destinations || []).length > 1 && (
              <select value={planFilters.destination} onChange={(e) => setPlanFilters({ ...planFilters, destination: e.target.value })} className="input w-auto">
                <option value="">All destinations</option>
                {(plan?.options?.destinations || []).map((d: string) => <option key={d} value={d}>{d}</option>)}
              </select>
            )}
            {(plan?.options?.sources || []).length > 1 && (
              <select value={planFilters.source} onChange={(e) => setPlanFilters({ ...planFilters, source: e.target.value })} className="input w-auto">
                <option value="">All order types</option>
                {(plan?.options?.sources || []).map((s: string) => <option key={s} value={s}>{s === 'shopify' ? 'Shopify' : s === 'subscription' ? 'Upcoming subscriptions' : s === 'manual' ? 'Added by hand' : s}</option>)}
              </select>
            )}
            {(planFilters.date || planFilters.start || planFilters.destination || planFilters.source) && (
              <button onClick={() => setPlanFilters({ date: '', start: '', end: '', destination: '', source: '' })} className="text-sm text-primary-600 font-medium">Clear</button>
            )}
          </div>

          {showEmailPlan && (
            <div className="card p-3 flex flex-wrap items-center gap-2">
              <button onClick={downloadPlanCsv} className="btn btn-sm btn-secondary shrink-0" title="CSV for Google Sheets — totals, flavors, batches">Download for Sheets</button>
              <span className="text-sm text-slate-500">Email {planFilters.date ? `${planDateLabel(planFilters.date, true)}${planIsMonthly ? "'s plan" : ''}` : planIsMonthly ? 'the full' : "today's"} breakdown to</span>
              <input value={emailPlanTo} onChange={(e) => setEmailPlanTo(e.target.value)} placeholder="name@email.com, name2@email.com" className="input flex-1 min-w-[220px] text-sm" />
              <button onClick={() => emailPlan.mutate()} disabled={emailPlan.isPending || !emailPlanTo.includes('@')} className="btn btn-sm btn-primary">{emailPlan.isPending ? 'Sending…' : 'Send'}</button>
              <button onClick={() => setShowEmailPlan(false)} className="btn btn-sm btn-ghost">Cancel</button>
            </div>
          )}

          {!plan || plan.totalUnits === 0 ? (
            isCpg && !storeConnected ? (
              /* Standalone wholesale brand: demand IS the open POs — no store needed. */
              <WholesaleMakePlan />
            ) : (
            <div className="card p-10 text-center text-slate-500">
              <Factory className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p>{plan && plan.orderCount === 0 && (planFilters.date || planFilters.destination || planFilters.source) ? 'Nothing matches these filters — clear them to see everything upcoming.' : 'Nothing to make yet — connect Shopify and Sync, or add an order.'}</p>
            </div>
            )
          ) : (
            <>
              {/* The split, in the order she reads it: what products, what
                  flavors, where it goes. Each line in units and batches. */}
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="card p-4">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <p className="text-xs text-slate-400 uppercase">By product{(plan.bufferPerItem || 0) > 0 ? ' → final' : ''}</p>
                    {/* Extras buffer — the +N she adds on top of each count for her Final Count */}
                    <div className="flex items-center gap-1 text-xs">
                      <span className="text-slate-400">Extras, each</span><span className="text-slate-500">+</span>
                      <input type="number" min={0} inputMode="numeric"
                        value={bufferDraft ?? String(plan.bufferPerItem ?? 0)}
                        onChange={(e) => setBufferDraft(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') saveBuffer.mutate(); }}
                        className="input w-14 text-sm py-0.5 text-right tabular-nums" />
                      {bufferDraft != null && bufferDraft !== String(plan.bufferPerItem ?? 0) && (
                        <button onClick={() => saveBuffer.mutate()} disabled={saveBuffer.isPending} className="btn btn-sm btn-primary py-0.5">{saveBuffer.isPending ? '…' : 'Save'}</button>
                      )}
                    </div>
                  </div>
                  {plan.byClass.map((r: any) => {
                    const make = (plan.bufferPerItem || 0) > 0 ? (r.final ?? (r.quantity + plan.bufferPerItem)) : r.quantity;
                    return (
                      <div key={r.name} className="flex justify-between items-baseline gap-2 text-sm py-0.5">
                        <span className="text-slate-600 capitalize">{r.name}</span>
                        <span className="tabular-nums text-right">
                          {(plan.bufferPerItem || 0) > 0 && <span className="text-slate-400">{r.quantity} + {plan.bufferPerItem} = </span>}
                          <span className="font-bold">{unitsLabel(make)}</span>
                          <span className="text-slate-500">{batchesOf(make)}</span>
                        </span>
                      </div>
                    );
                  })}
                  {!plan.batchYield && (
                    <p className="text-xs text-slate-400 mt-2">Units only for now — set a batch size in Settings and these show as batches too.</p>
                  )}
                </div>
                <div className="card p-4">
                  <p className="text-xs text-slate-400 uppercase mb-2">By flavor{(plan.bufferPerItem || 0) > 0 ? ' → final' : ''}</p>
                  {plan.byFlavor.length === 0 ? <p className="text-sm text-slate-400">—</p> : plan.byFlavor.map((r: any) => {
                    const make = (plan.bufferPerItem || 0) > 0 ? (r.final ?? (r.quantity + plan.bufferPerItem)) : r.quantity;
                    return (
                      <div key={r.name} className="flex justify-between items-baseline gap-2 text-sm py-0.5">
                        <span className="text-slate-600">{r.name}
                          {r.name.toLowerCase().includes('flavor of the week') && (
                            <button onClick={() => { setFotwName(''); setFotwEditing(true); }} className="ml-1.5 text-xs text-primary-600 font-medium" title="Name this week's flavor">{r.name.includes('—') ? 'change' : 'name it'}</button>
                          )}
                        </span>
                        <span className="tabular-nums text-right">
                          {(plan.bufferPerItem || 0) > 0 && <span className="text-slate-400">{r.quantity} + {plan.bufferPerItem} = </span>}
                          <span className="font-bold">{unitsLabel(make)}</span>
                          <span className="text-slate-500">{batchesOf(make)}</span>
                        </span>
                      </div>
                    );
                  })}
                  {fotwEditing && (
                    <div className="flex items-center gap-2 mt-2">
                      <input value={fotwName} onChange={(e) => setFotwName(e.target.value)} placeholder="This week's flavor, e.g. cinnamon" className="input flex-1 text-sm" autoFocus />
                      <button onClick={() => saveFotw.mutate()} disabled={!fotwName.trim() || saveFotw.isPending} className="btn btn-sm btn-primary">{saveFotw.isPending ? '…' : 'Save'}</button>
                      <button onClick={() => setFotwEditing(false)} className="btn btn-sm btn-ghost">Cancel</button>
                    </div>
                  )}
                </div>
                <div className="card p-4">
                  <p className="text-xs text-slate-400 uppercase mb-2">By destination</p>
                  {plan.byDestination.length === 0 ? <p className="text-sm text-slate-400">—</p> : plan.byDestination.map((r: any) => (
                    <div key={r.name} className="flex justify-between items-baseline gap-2 text-sm py-0.5">
                      <span className={clsx(r.name === 'Unassigned' ? 'text-amber-700' : 'text-slate-600')}>{r.name}</span>
                      <span className="font-bold tabular-nums">{unitsLabel(r.quantity)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {(plan.orderRows || []).length > 0 && (
                <div>
                  <p className="text-xs text-slate-400 uppercase mb-2">Every order in this plan</p>
                <div className="card overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium text-slate-500">Customer</th>
                        <th className="text-left px-3 py-2 font-medium text-slate-500">Lane</th>
                        {(plan.byClass || []).map((c: any) => <th key={c.name} className="text-right px-3 py-2 font-medium text-slate-500">{c.name}</th>)}
                        <th className="text-right px-3 py-2 font-semibold text-slate-700">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {(plan.orderRows || []).map((r: any) => (
                        <tr key={r.id} className="hover:bg-slate-50 group">
                          <td className="px-3 py-1.5 text-slate-900">
                            <span className="inline-flex items-center gap-1.5">
                              {r.customer}{r.source === 'subscription' && <span className="text-[10px] text-violet-500">sub</span>}
                              <button
                                onClick={() => {
                                  const full = (orders as any[]).find((o: any) => o.id === r.id);
                                  if (!full) return;
                                  setOrderSearch(''); setShowPastOrders(true); beginEdit(full); setTab('orders');
                                }}
                                className="opacity-0 group-hover:opacity-100 p-0.5 text-slate-400 hover:text-primary-600" title="Fix this order — opens it for editing">
                                <Pencil className="w-3 h-3" />
                              </button>
                            </span>
                          </td>
                          <td className="px-3 py-1.5 text-slate-500">{r.destination}</td>
                          {(plan.byClass || []).map((c: any) => <td key={c.name} className="px-3 py-1.5 text-right tabular-nums">{r.counts?.[c.name] || ''}</td>)}
                          <td className="px-3 py-1.5 text-right font-semibold tabular-nums">{r.total}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-slate-50 border-t">
                      <tr>
                        <td className="px-3 py-2 font-semibold text-slate-900">Total · {plan.orderCount} orders</td>
                        <td />
                        {(plan.byClass || []).map((c: any) => <td key={c.name} className="px-3 py-2 text-right font-semibold tabular-nums">{c.quantity}</td>)}
                        <td className="px-3 py-2 text-right font-bold tabular-nums">{plan.totalUnits}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                </div>
              )}

              {/* Unmapped products — plainly: what it is, why, and the one action */}
              {plan.exceptions.length > 0 && (
                <div className="card p-4 border-amber-200 bg-amber-50">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                      <p className="font-medium text-amber-900">{plan.exceptions.length} product{plan.exceptions.length > 1 ? 's aren\'t' : " isn't"} in the count yet</p>
                    </div>
                    <button onClick={() => applySuggestions.mutate()} disabled={applySuggestions.isPending} className="btn btn-sm btn-primary shrink-0">{applySuggestions.isPending ? 'Mapping…' : 'Map them all'}</button>
                  </div>
                  <p className="text-xs text-amber-700 mb-3">Mise counts an order once it knows what each product <em>becomes</em> (e.g. a "duo" = 1 classic + 1 flavor). Tap <span className="font-medium">Map them all</span> to accept the suggestions below — you only do this once per new product.</p>
                  <div className="space-y-2">
                    {plan.exceptions.map((e: any, i: number) => (
                      <div key={i} className="flex items-center justify-between gap-2 text-sm">
                        <div>
                          <span className="text-amber-900">{e.quantity}× <span className="font-medium">{e.sourceName}</span></span>
                          {e.suggested?.length ? <span className="block text-xs text-amber-700">becomes → {suggestLabel(e.suggested)}</span> : null}
                        </div>
                        <button onClick={() => mapException(e.sourceName, e.suggested)} className="btn btn-sm btn-secondary shrink-0">Change</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Unassigned destination — lists the exact orders, each editable */}
              {plan.unassignedOrders?.length > 0 && (
                <div className="card p-4 border-amber-200 bg-amber-50">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                    <p className="font-medium text-amber-900">{plan.unassignedOrders.length} order{plan.unassignedOrders.length > 1 ? 's have' : ' has'} no destination</p>
                  </div>
                  <p className="text-xs text-amber-700 mb-3">These can't be sorted by lane until you say where they go. Tap <span className="font-medium">Set</span> to add a destination (or add a delivery tag/address in Shopify and re-sync).</p>
                  <div className="space-y-2">
                    {plan.unassignedOrders.map((u: any) => (
                      <div key={u.id} className="flex items-center justify-between gap-2 text-sm">
                        <div className="min-w-0">
                          <span className="text-amber-900 font-medium">{u.customerName || 'Order'}</span>
                          <span className="block text-xs text-amber-700 truncate">{u.summary}{u.fulfillmentDate ? ` · due ${fmtDate(u.fulfillmentDate)}` : ''}</span>
                        </div>
                        <button
                          onClick={() => { const full = orders.find((o: any) => o.id === u.id); if (full) beginEdit(full); setTab('orders'); }}
                          className="btn btn-sm btn-secondary shrink-0"
                        >Set</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* A brand's prep is monthly production, not a weekday checklist:
              projected demand per SKU vs stock on hand, from wholesale orders. */}
          {isCpgAccount && <CpgProductionOutlook />}

          {/* Tomorrow's prep — forecast walked through recipes into a checklist (restaurants/makers) */}
          {!isCpgAccount && (prep?.tasks || []).length > 0 && (
            <div className="card p-5">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="text-sm font-medium text-slate-900">
                    Prep for {new Date(prep.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long' })}
                  </p>
                  <p className="text-xs text-slate-500">
                    From your last {prep.basedOnWeeks} {new Date(prep.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long' })}s (~{prep.expectedOrders} orders expected{prep.catering > 0 ? `, plus ${prep.catering} catering order${prep.catering === 1 ? '' : 's'}` : ''}) — ready before open and the morning runs itself.
                  </p>
                </div>
                <button className="text-xs text-primary-600 font-medium" onClick={() => setPrepOpen((o) => !o)}>{prepOpen ? 'Hide' : 'Show'}</button>
              </div>
              {prepOpen && (
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5">
                  {(prep.tasks as any[]).slice(0, 14).map((t: any) => {
                    const qty = ['each', 'unit', 'count', 'piece'].includes(String(t.unit).toLowerCase()) ? Math.round(t.quantity) : t.quantity;
                    const key = `${prep.date}:${t.itemId}`;
                    const done = !!prepDone[key];
                    return (
                      <label key={t.itemId} className="flex items-baseline justify-between gap-3 border-b border-slate-50 pb-1 cursor-pointer">
                        <span className={`text-sm flex items-center gap-2 ${done ? 'text-slate-300 line-through' : 'text-slate-800'}`}>
                          <input type="checkbox" checked={done} onChange={() => togglePrep(key)} />
                          {t.name}
                        </span>
                        <span className={`text-sm tabular-nums font-semibold whitespace-nowrap ${done ? 'text-slate-300' : 'text-slate-900'}`}>{qty} {t.unit}</span>
                      </label>
                    );
                  })}
                </div>
              )}
              {prepOpen && (prep.tasks as any[]).some((t: any) => t.drivers?.some((d: any) => d.menu.startsWith('Catering'))) && (
                <p className="text-xs text-amber-700 mt-2">Includes catering due that day.</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* ---- ORDERS ---- */}

      {tab === 'orders' && (
        <div className="space-y-4">
          {/* Shopify connection — available to every maker; a CPG's D2C channel
              is often a Shopify store. */}
          {(
          <div className="card p-4">
            {shopify?.connected ? (
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-green-50 flex items-center justify-center"><ShoppingBag className="w-5 h-5 text-green-600" /></div>
                  <div>
                    <p className="font-medium text-slate-900 flex items-center gap-1.5"><Check className="w-4 h-4 text-green-600" /> {shopify.shopDomain}</p>
                    <p className="text-xs text-slate-400">Last synced {shopify.lastSyncAt ? new Date(shopify.lastSyncAt).toLocaleString('en-US') : 'never'}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => syncShopify.mutate()} disabled={syncShopify.isPending} className="btn btn-primary flex items-center gap-2">
                    <RefreshCw className={clsx('w-4 h-4', syncShopify.isPending && 'animate-spin')} /> {syncShopify.isPending ? 'Syncing…' : 'Sync now'}
                  </button>
                  <button onClick={() => disconnectShopify.mutate()} className="btn btn-ghost text-slate-500">Disconnect</button>
                </div>
              </div>
            ) : !showConnect ? (
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center"><ShoppingBag className="w-5 h-5 text-slate-400" /></div>
                  <div>
                    <p className="font-medium text-slate-900">Connect Shopify</p>
                    <p className="text-xs text-slate-400">Pull your open orders in automatically — no more copying by hand.</p>
                  </div>
                </div>
                <button onClick={() => setShowConnect(true)} className="btn btn-secondary">Connect store</button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2"><ShoppingBag className="w-4 h-4 text-slate-400" /><p className="font-medium text-slate-900">Connect your Shopify store</p></div>
                <input value={connForm.shopDomain} onChange={(e) => setConnForm({ ...connForm, shopDomain: e.target.value })} placeholder="Store domain, e.g. your-store.myshopify.com" className="input w-full" />
                <p className="text-xs text-slate-500">Created an app for your store in Shopify’s Dev Dashboard? <a href="/settings?tab=integrations" className="underline">Connect it with its client ID and secret in Settings → Integrations.</a></p>
                {shopify?.oauthAvailable ? (
                  // OAuth path: approve Mise in the Shopify admin, no token pasting.
                  <>
                    <p className="text-xs text-slate-400">You'll be sent to Shopify to approve access — orders sync automatically from then on. Upcoming subscription renewals are projected from your recurring orders, so next week's subscribers show up in the plan before they bill.</p>
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => { setShowConnect(false); setConnForm({ shopDomain: '', accessToken: '' }); }} className="btn btn-ghost">Cancel</button>
                      <button onClick={() => startShopifyOauth.mutate()} disabled={!connForm.shopDomain.trim() || startShopifyOauth.isPending} className="btn btn-primary">{startShopifyOauth.isPending ? 'Redirecting…' : 'Connect with Shopify'}</button>
                    </div>
                    <details className="text-xs text-slate-400">
                      <summary className="cursor-pointer">Have an existing legacy access token?</summary>
                      <div className="mt-2 space-y-2">
                        <input value={connForm.accessToken} onChange={(e) => setConnForm({ ...connForm, accessToken: e.target.value })} placeholder="Admin API access token (starts with shpat_)" className="input w-full" type="password" />
                        <p>Paste a permanent Admin API token from an older custom app with read_orders access. For a new app with a client ID and secret, use Settings → Integrations.</p>
                        <div className="flex justify-end"><button onClick={() => connectShopify.mutate()} disabled={!connForm.shopDomain.trim() || !connForm.accessToken.trim() || connectShopify.isPending} className="btn btn-sm btn-secondary">{connectShopify.isPending ? 'Connecting…' : 'Connect with token'}</button></div>
                      </div>
                    </details>
                  </>
                ) : (
                  <>
                    <input value={connForm.accessToken} onChange={(e) => setConnForm({ ...connForm, accessToken: e.target.value })} placeholder="Admin API access token (starts with shpat_)" className="input w-full" type="password" />
                    <p className="text-xs text-slate-400">Paste a permanent Admin API token from an older custom app with read_orders access. For a new app with a client ID and secret, use Settings → Integrations.</p>
                    <p className="text-xs text-slate-400">Upcoming subscription renewals are projected automatically from your recurring orders — so next week's subscribers show up in the plan before they bill.</p>
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => { setShowConnect(false); setConnForm({ shopDomain: '', accessToken: '' }); }} className="btn btn-ghost">Cancel</button>
                      <button onClick={() => connectShopify.mutate()} disabled={!connForm.shopDomain.trim() || !connForm.accessToken.trim() || connectShopify.isPending} className="btn btn-primary">{connectShopify.isPending ? 'Connecting…' : 'Connect'}</button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
          )}

          <div className="flex items-center gap-2">
            <input value={orderSearch} onChange={(e) => setOrderSearch(e.target.value)} placeholder="Search a customer, product or lane… (looks through past orders too)" className="input flex-1" />
            {orderSearch && <button onClick={() => setOrderSearch('')} className="text-sm text-primary-600 font-medium shrink-0">Clear</button>}
          </div>
          {!showOrderForm ? (
            <button onClick={() => { setOrderForm({ ...emptyOrder, fulfillmentDate: today }); setShowOrderForm(true); }} className="btn btn-primary flex items-center gap-2"><Plus className="w-4 h-4" /> Add order</button>
          ) : (
            <div className="card p-4 space-y-3">
              <p className="text-sm text-slate-600">A hand-added order counts in the plan and the delivery lanes exactly like a synced one — wholesale, an event, a big Hamptons drop.</p>
              <div className="grid sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-500 block mb-1">Who is it for?</label>
                  <input value={orderForm.customerName} onChange={(e) => setOrderForm({ ...orderForm, customerName: e.target.value })} placeholder="Customer or event (optional)" className="input w-full" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 block mb-1">Which lane does it go out on?</label>
                  <input list="known-lanes" value={orderForm.destination} onChange={(e) => setOrderForm({ ...orderForm, destination: e.target.value })} placeholder="Pick a lane or type a new one" className="input w-full" />
                  <datalist id="known-lanes">
                    {(plan?.options?.destinations || []).filter((d: string) => d !== 'Unassigned' && d !== 'Subscriptions').map((d: string) => <option key={d} value={d} />)}
                  </datalist>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500 block mb-1">Bake / delivery date</label>
                  <input type="date" value={orderForm.fulfillmentDate} onChange={(e) => setOrderForm({ ...orderForm, fulfillmentDate: e.target.value })} className="input w-full" />
                </div>
              </div>
              <p className="text-xs text-slate-400">Use the same product names as your store so the plan counts them right — a new name shows up once as "unmapped" and you tell Mise what it bakes as.</p>
              {orderForm.lines.map((l, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input list="known-products" value={l.sourceName} onChange={(e) => { const r = [...orderForm.lines]; r[i] = { ...r[i], sourceName: e.target.value }; setOrderForm({ ...orderForm, lines: r }); }} placeholder="Pick a product — or type your own" className="input flex-1" />
                  <input type="number" min="0" step="1" value={l.quantity || ''} onChange={(e) => { const r = [...orderForm.lines]; r[i] = { ...r[i], quantity: parseFloat(e.target.value) || 0 }; setOrderForm({ ...orderForm, lines: r }); }} placeholder="qty" className="input w-24" />
                  <button onClick={() => setOrderForm({ ...orderForm, lines: orderForm.lines.filter((_, x) => x !== i) })} className="p-2 text-red-400 hover:bg-red-50 rounded-lg"><X className="w-4 h-4" /></button>
                </div>
              ))}
              <div className="flex items-center justify-between">
                <button onClick={() => setOrderForm({ ...orderForm, lines: [...orderForm.lines, { sourceName: '', quantity: 0 }] })} className="text-sm text-primary-600 font-medium">+ Add line</button>
                <div className="flex gap-2">
                  <button onClick={() => setShowOrderForm(false)} className="btn btn-ghost">Cancel</button>
                  <button onClick={() => addOrder.mutate()} className="btn btn-primary" disabled={!orderForm.lines.some((l) => l.sourceName && l.quantity > 0) || addOrder.isPending}>{addOrder.isPending ? 'Adding…' : 'Add order'}</button>
                </div>
              </div>
            </div>
          )}

          {orders.length === 0 ? (
            <div className="card p-10 text-center text-slate-500">No orders yet. Connect Shopify above to pull them in automatically, or add one by hand.</div>
          ) : (() => {
            // Forecast-loaded rows are plans, not customer orders — they get their
            // own clearly-labeled section so the list reads honestly.
            const planned = orders.filter((o: any) => o.source === 'forecast');
            const todayKey = new Date().toISOString().slice(0, 10);
            const allReal = orders.filter((o: any) => o.source !== 'forecast');
            const isPast = (o: any) => o.fulfillmentDate && String(o.fulfillmentDate).slice(0, 10) < todayKey;
            const pastOrders = allReal.filter(isPast);
            const q = orderSearch.trim().toLowerCase();
            const matches = (o: any) => !q || [o.customerName, o.destination, ...(o.lines || []).map((l: any) => l.sourceName)]
              .some((v: any) => String(v || '').toLowerCase().includes(q));
            const real = (q ? allReal : showPastOrders ? allReal : allReal.filter((o: any) => !isPast(o)))
              .filter(matches)
              .sort((a: any, b: any) => String(a.fulfillmentDate || '9999').localeCompare(String(b.fulfillmentDate || '9999')));
            const byMonth: Record<string, any[]> = {};
            for (const o of planned) (byMonth[String(o.fulfillmentDate || '').slice(0, 7)] ||= []).push(o);
            const renderOrderCard = (o: any) => (
                <div key={o.id} className="card p-4">
                  {editOrderId === o.id ? (
                    <div className="space-y-3">
                      <div className="grid sm:grid-cols-3 gap-2">
                        <input value={editForm.customerName} onChange={(e) => setEditForm({ ...editForm, customerName: e.target.value })} placeholder="Customer" className="input" />
                        <input value={editForm.destination} onChange={(e) => setEditForm({ ...editForm, destination: e.target.value })} placeholder="Destination (lane)" className="input" />
                        <input type="date" value={editForm.fulfillmentDate} onChange={(e) => setEditForm({ ...editForm, fulfillmentDate: e.target.value })} className="input" />
                      </div>
                      <input value={editForm.deliveryAddress} onChange={(e) => setEditForm({ ...editForm, deliveryAddress: e.target.value })} placeholder="Delivery address (street, city, state, zip)" className="input w-full" />
                      {editForm.lines.map((l, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <input list="known-products" value={l.sourceName} onChange={(e) => { const r = [...editForm.lines]; r[i] = { ...r[i], sourceName: e.target.value }; setEditForm({ ...editForm, lines: r }); }} placeholder="Pick a product — or type your own" className="input flex-1" />
                          <input type="number" min="0" step="1" value={l.quantity || ''} onChange={(e) => { const r = [...editForm.lines]; r[i] = { ...r[i], quantity: parseFloat(e.target.value) || 0 }; setEditForm({ ...editForm, lines: r }); }} placeholder="qty" className="input w-20" />
                          <button onClick={() => setEditForm({ ...editForm, lines: editForm.lines.filter((_, x) => x !== i) })} className="p-2 text-red-400 hover:bg-red-50 rounded-lg"><X className="w-4 h-4" /></button>
                        </div>
                      ))}
                      <div className="flex items-center justify-between">
                        <button onClick={() => setEditForm({ ...editForm, lines: [...editForm.lines, { sourceName: '', quantity: 0 }] })} className="text-sm text-primary-600 font-medium">+ Add line</button>
                        <div className="flex gap-2">
                          <button onClick={() => setEditOrderId(null)} className="btn btn-ghost">Cancel</button>
                          <button onClick={() => saveOrder.mutate()} disabled={saveOrder.isPending} className="btn btn-primary">{saveOrder.isPending ? 'Saving…' : 'Save'}</button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-900 flex items-center gap-1.5">{o.customerName || 'Order'} {o.destination ? <span className="text-slate-400">· {o.destination}</span> : null}{o.source === 'shopify' ? <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-green-50 text-green-700"><ShoppingBag className="w-3 h-3" /> Shopify</span> : null}{o.source === 'subscription' ? <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-violet-50 text-violet-700"><RefreshCw className="w-3 h-3" /> Upcoming subscription</span> : null}</p>
                        <p className="text-sm text-slate-500">{(o.lines || []).map((l: any) => `${parseFloat(l.quantity)}× ${l.sourceName}`).join(', ')} · due {fmtDate(o.fulfillmentDate)}</p>
                        {(() => {
                          const { units, unmappedQty } = orderUnits(o);
                          if (units === 0 && unmappedQty === 0) return null;
                          return (
                            <p className="text-xs text-slate-400">
                              {units > 0 ? `${unitsLabel(units)}${orderBatchesLabel(units)}` : ''}
                              {unmappedQty > 0 ? `${units > 0 ? ' · ' : ''}${unmappedQty} not mapped yet` : ''}
                            </p>
                          );
                        })()}
                      </div>
                      <div className="flex items-center shrink-0">
                        <button onClick={() => beginEdit(o)} className="p-2 text-slate-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => deleteOrder.mutate(o.id)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                  )}
                </div>
            );
            return (
              <div className="space-y-4">
                {real.length > 0 && <div className="space-y-2">{real.map(renderOrderCard)}</div>}
                {pastOrders.length > 0 && (
                  <button onClick={() => setShowPastOrders((v) => !v)} className="text-sm text-slate-400 hover:text-slate-600">
                    {showPastOrders ? 'Hide past orders' : `Show ${pastOrders.length} past order${pastOrders.length === 1 ? '' : 's'} (already fulfilled)`}
                  </button>
                )}
                {planned.length > 0 && (
                  <div>
                    <div className="mb-2">
                      <p className="font-semibold text-slate-900 flex items-center gap-1.5"><FileSpreadsheet className="w-4 h-4 text-slate-500" /> Planned production — from your Demand Forecast</p>
                      <p className="text-xs text-slate-500">Not customer orders: these are the remaining months of your own forecast sheet, one line per product per month, loaded so "What to make" and the Supply plan can see the rest of the year. Edit or delete them like any order.</p>
                    </div>
                    <div className="grid md:grid-cols-2 gap-2">
                      {Object.keys(byMonth).sort().map((ym) => (
                        <div key={ym} className="card p-4">
                          <p className="text-sm font-semibold text-slate-900 mb-2">{new Date(ym + '-15T12:00:00').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</p>
                          <div className="space-y-1.5">
                            {byMonth[ym].map((o: any) => editOrderId === o.id ? <div key={o.id}>{renderOrderCard(o)}</div> : (
                              <div key={o.id} className="flex items-center justify-between text-sm">
                                <span className="text-slate-700">{(o.lines || []).map((l: any) => `${parseFloat(l.quantity).toLocaleString('en-US')}× ${l.sourceName}`).join(', ')}{(() => { const { units } = orderUnits(o); return units > 0 ? <span className="text-slate-400"> · {unitsLabel(units)}{orderBatchesLabel(units)}</span> : null; })()}</span>
                                <span className="flex items-center shrink-0">
                                  <button onClick={() => beginEdit(o)} className="p-1.5 text-slate-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg"><Pencil className="w-3.5 h-3.5" /></button>
                                  <button onClick={() => deleteOrder.mutate(o.id)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg"><Trash2 className="w-3.5 h-3.5" /></button>
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* ---- DELIVERIES ---- */}
      {tab === 'deliveries' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <input value={stopSearch} onChange={(e) => setStopSearch(e.target.value)} placeholder="Find a customer or address…" className="input w-52" />
              <button onClick={printBagLabels} className="btn btn-sm btn-secondary shrink-0" title="One sticker per bag — name, order, address — onto Avery 8160 sheets (30 per page)">Print bag labels</button>
              <div className="flex items-center gap-1.5">
                <CalendarDays className="w-4 h-4 text-slate-400" />
                <select value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} className="input w-auto">
                  <option value="">All dates (incl. past)</option>
                  {(() => {
                    const today = new Date().toISOString().slice(0, 10);
                    const ds = (deliveries?.dates || []) as string[];
                    // Upcoming days first; earlier days only when one is selected
                    // (so a past pick stays visible in the control).
                    const shown = ds.filter((d) => d >= today || d === deliveryDate);
                    return shown.map((d) => (
                      <option key={d} value={d}>{new Date(d + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}{d < today ? ' (past)' : ''}</option>
                    ));
                  })()}
                </select>
              </div>
              {(() => {
                const all = (deliveries?.byDestination || []).flatMap((g: any) => g.orders || []);
                const sources = [...new Set(all.map((o: any) => o.source))].filter(Boolean) as string[];
                const rhCount = all.filter((o: any) => RH_RE.test(o.items || '')).length;
                const label = (s: string) => (s === 'shopify' ? 'Shopify' : s === 'subscription' ? 'Subscriptions' : 'Added by hand');
                return (<>
                  {sources.length > 1 && (
                    <select value={deliverySource} onChange={(e) => setDeliverySource(e.target.value)} className="input w-auto">
                      <option value="">All order types</option>
                      {sources.map((s) => <option key={s} value={s}>{label(s)}</option>)}
                    </select>
                  )}
                  {rhCount > 0 && (
                    <button onClick={() => setRhOnly((v) => !v)}
                      className={clsx('px-3 py-1.5 rounded-full text-sm font-medium border', rhOnly ? 'bg-amber-100 text-amber-800 border-amber-200' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50')}>
                      🍎 Rosh Hashanah ({rhCount})
                    </button>
                  )}
                  {(deliverySource || rhOnly) && (
                    <button onClick={() => { setDeliverySource(''); setRhOnly(false); }} className="text-sm text-primary-600 font-medium">Clear</button>
                  )}
                </>);
              })()}
            </div>
            {deliveries && deliveries.totalOrders > 0 && (() => {
              const all = (deliveries.byDestination || []).flatMap((g: any) => g.orders || []).filter(stopVisible);
              const done = all.filter((o: any) => o.deliveryStatus === 'delivered').length;
              return <p className="text-sm text-slate-500">{done} of {all.length} delivered{(deliverySource || rhOnly) ? ' (filtered)' : ''}</p>;
            })()}
          </div>

          {!deliveries || deliveries.totalOrders === 0 ? (
            <div className="card p-10 text-center text-slate-500"><MapPin className="w-10 h-10 mx-auto mb-2 opacity-30" /><p>No deliveries for this date. Sync Shopify or add an order.</p></div>
          ) : (
            deliveries.byDestination.map((g: any) => {
              const withAddr = g.orders.filter((o: any) => o.deliveryAddress).length;
              const shown = g.orders.filter(stopVisible);
              if ((deliverySource || rhOnly) && shown.length === 0) return null; // lane empty under filter
              const shownDone = shown.filter((o: any) => o.deliveryStatus === 'delivered').length;
              return (
              <div key={g.destination} className="card p-4">
                <div className="flex items-center justify-between gap-2 mb-3">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-primary-600" />
                    <p className="font-semibold text-slate-900">{g.destination}</p>
                    <span className="text-xs text-slate-400">{(deliverySource || rhOnly) ? `${shownDone}/${shown.length} delivered (filtered)` : `${g.delivered}/${g.total} delivered`}</span>
                  </div>
                  {withAddr >= 2 && routeLane !== g.destination && (
                    <div className="flex items-center gap-2 shrink-0">
                      {withAddr < g.orders.length && <span className="text-xs text-amber-600">{g.orders.length - withAddr} stop{g.orders.length - withAddr === 1 ? '' : 's'} missing address</span>}
                      <button onClick={() => { setRouteLane(g.destination); setRouteForm({ startAddress: '', email: '' }); }} className="btn btn-sm btn-secondary">Create route</button>
                    </div>
                  )}
                  {withAddr < 2 && g.orders.length > 1 && (
                    <span className="text-xs text-slate-400 shrink-0" title="Stops here carry no delivery addresses — customers collect in person. Nothing to route.">pickup point — no route needed</span>
                  )}
                </div>
                {routeLane === g.destination && (
                  <div className="mb-3 p-3 rounded-xl bg-slate-50 border border-slate-200 space-y-2">
                    <p className="text-sm font-medium text-slate-700">Optimized route for {withAddr} stop{withAddr === 1 ? '' : 's'} with an address</p>
                    <input value={routeForm.startAddress} onChange={(e) => setRouteForm({ ...routeForm, startAddress: e.target.value })} placeholder="Start address (e.g. the bakery)" className="input w-full" />
                    <input value={routeForm.email} onChange={(e) => setRouteForm({ ...routeForm, email: e.target.value })} placeholder="Email to (comma-separate for several drivers)" className="input w-full" />
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => setRouteLane(null)} className="btn btn-ghost btn-sm">Cancel</button>
                      <button onClick={() => createRoute.mutate()} disabled={!routeForm.startAddress.trim() || !routeForm.email.trim() || createRoute.isPending} className="btn btn-primary btn-sm">{createRoute.isPending ? 'Building & sending…' : 'Optimize & email'}</button>
                    </div>
                  </div>
                )}
                <div className="divide-y divide-slate-50">
                  {shown.map((o: any) => {
                    const next = o.deliveryStatus === 'pending' ? 'out' : o.deliveryStatus === 'out' ? 'delivered' : 'pending';
                    const badge = o.deliveryStatus === 'delivered' ? 'bg-emerald-50 text-emerald-700' : o.deliveryStatus === 'out' ? 'bg-blue-50 text-blue-700' : 'bg-slate-100 text-slate-500';
                    const label = o.deliveryStatus === 'delivered' ? 'Delivered' : o.deliveryStatus === 'out' ? 'Out for delivery' : 'Pending';
                    if (editStop && editStop.id === o.id) {
                      const es = editStop;
                      return (
                        <div key={o.id} className="py-2.5 space-y-2">
                          <input value={es.customerName} onChange={(e) => setEditStop({ ...es, customerName: e.target.value })} placeholder="Customer" className="input w-full" />
                          <input value={es.address} onChange={(e) => setEditStop({ ...es, address: e.target.value })} placeholder="Delivery address (street, city, state, zip)" className="input w-full" />
                          <p className="text-[11px] text-slate-400">A precise address (with zip, no apartment quirks) routes best.</p>
                          <div className="flex gap-2 justify-end">
                            <button onClick={() => setEditStop(null)} className="btn btn-ghost btn-sm">Cancel</button>
                            <button onClick={() => saveStop.mutate()} disabled={saveStop.isPending} className="btn btn-primary btn-sm">{saveStop.isPending ? 'Saving…' : 'Save'}</button>
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div key={o.id} className="py-2.5 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className={clsx('text-sm font-medium', (o.units || 0) > 1 ? 'text-amber-800' : 'text-slate-900')}>
                            {o.routeOrder != null && <span className="mr-1.5 text-[11px] font-semibold text-primary-700 bg-primary-50 rounded-full px-1.5 py-0.5" title="Position on the optimized route — pack in this order">#{o.routeOrder}</span>}
                            {o.customerName || 'Order'}
                            {(o.units || 0) > 1 && <span className="ml-1.5 text-[11px] font-semibold bg-amber-100 text-amber-800 rounded-full px-1.5 py-0.5" title="More than one item — goes in a bag">🛍 bag</span>}
                          </p>
                          <p className="text-xs text-slate-500 truncate">{o.items}{o.deliveryAddress ? ` · ${o.deliveryAddress}` : ' · no address'}</p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {confirmDelStop === o.id ? (
                            <>
                              <span className="text-xs text-slate-500">Remove?</span>
                              <button onClick={() => { deleteStop.mutate(o.id); setConfirmDelStop(null); }} disabled={deleteStop.isPending} className="text-xs px-2 py-1 rounded-full font-medium bg-red-100 text-red-700 hover:bg-red-200">Remove</button>
                              <button onClick={() => setConfirmDelStop(null)} className="text-xs px-2 py-1 rounded-full font-medium text-slate-500 hover:bg-slate-100">Keep</button>
                            </>
                          ) : (
                            <>
                              <button onClick={() => setEditStop({ id: o.id, customerName: o.customerName || '', address: o.deliveryAddress || '' })} className="p-1.5 text-slate-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg" title="Edit stop"><Pencil className="w-3.5 h-3.5" /></button>
                              <button onClick={() => setConfirmDelStop(o.id)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg" title="Remove stop"><Trash2 className="w-3.5 h-3.5" /></button>
                              <button onClick={() => setDelivery.mutate({ id: o.id, status: next })} className={clsx('text-xs px-2.5 py-1 rounded-full font-medium', badge)} title="Tap to advance">
                                {label}
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              );
            })
          )}
        </div>
      )}

      {/* ---- MAPPINGS ---- */}
      {tab === 'mappings' && (
        <div className="space-y-4">
          <div className="card p-4 bg-slate-50 border-slate-100">
            <p className="text-sm text-slate-700">This is the translation table between <span className="font-medium">what customers order</span> and <span className="font-medium">what you bake</span>. Every count on the plan starts here.</p>
            <p className="text-sm text-slate-500 mt-1.5">Example: an order for one <span className="font-medium">"Challah Duo"</span> counts as <span className="font-medium">1 classic + 1 flavor of the week</span>. Set each product once — every order of it counts right forever.</p>
          </div>
          {!showMapForm ? (
            <button onClick={() => { setMapForm(emptyMap); setShowMapForm(true); }} className="btn btn-primary flex items-center gap-2"><Plus className="w-4 h-4" /> Add mapping</button>
          ) : (
            <div className="card p-4 space-y-3">
              <label className="text-xs font-medium text-slate-500 block">When an order says…</label>
              <input value={mapForm.sourceName} onChange={(e) => setMapForm({ ...mapForm, sourceName: e.target.value })} placeholder="The product name exactly as it appears on orders, e.g. Challah Duo" className="input" />
              <label className="text-xs font-medium text-slate-500 block">…you bake:</label>
              <div className="grid grid-cols-[1fr_1fr_5rem_2rem] gap-2 text-[11px] text-slate-400 px-1"><span>Type (classic / round…)</span><span>Flavor, if it has one</span><span>How many</span><span /></div>
              {mapForm.outputs.map((o, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input value={o.className} onChange={(e) => { const r = [...mapForm.outputs]; r[i] = { ...r[i], className: e.target.value }; setMapForm({ ...mapForm, outputs: r }); }} placeholder="Product class (e.g. plain)" className="input flex-1" />
                  <input value={o.flavor} onChange={(e) => { const r = [...mapForm.outputs]; r[i] = { ...r[i], flavor: e.target.value }; setMapForm({ ...mapForm, outputs: r }); }} placeholder="Flavor (optional)" className="input flex-1" />
                  <input type="number" min="1" step="1" value={o.unitsPerQty} onChange={(e) => { const r = [...mapForm.outputs]; r[i] = { ...r[i], unitsPerQty: parseFloat(e.target.value) || 1 }; setMapForm({ ...mapForm, outputs: r }); }} className="input w-20" title="How many of this per unit ordered" />
                  <button onClick={() => setMapForm({ ...mapForm, outputs: mapForm.outputs.filter((_, x) => x !== i) })} className="p-2 text-red-400 hover:bg-red-50 rounded-lg"><X className="w-4 h-4" /></button>
                </div>
              ))}
              <div className="flex items-center justify-between">
                <button onClick={() => setMapForm({ ...mapForm, outputs: [...mapForm.outputs, { className: '', flavor: '', unitsPerQty: 1 }] })} className="text-sm text-primary-600 font-medium">+ Add output</button>
                <div className="flex gap-2">
                  <button onClick={() => setShowMapForm(false)} className="btn btn-ghost">Cancel</button>
                  <button onClick={() => addMapping.mutate()} className="btn btn-primary" disabled={!mapForm.sourceName.trim() || !mapForm.outputs.some((o) => o.className) || addMapping.isPending}>{addMapping.isPending ? 'Saving…' : 'Save mapping'}</button>
                </div>
              </div>
            </div>
          )}
          {mappings.length === 0 ? (
            <div className="card p-10 text-center text-slate-500"><Tag className="w-10 h-10 mx-auto mb-2 opacity-30" /><p>No mappings yet. Add one for each product you sell.</p></div>
          ) : (
            <div className="space-y-2">
              {mappings.map((m: any) => (
                <div key={m.id} className="card p-4 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-900">"{m.sourceName}"</p>
                    <p className="text-sm text-slate-500">counts as {(m.outputs || []).map((o: any) => `${o.unitsPerQty} ${o.className}${o.flavor ? ` (${o.flavor})` : ''}`).join(' + ')}</p>
                  </div>
                  <button onClick={() => deleteMapping.mutate(m.id)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
